import { createContext, useContext, useMemo, useRef, type ReactNode, type MutableRefObject } from "react"
import type { InstanceWorkspaceSeed } from "../hooks/use-instance-workspace-loader"

// Shared wiring for hybrid persistence (APP-2705) between the seed/sync layer and the layout engine:
//   - currentSeed: the current dashboard's seed (arrangement for the engine's load(), and whether
//     the load succeeded, which gates the writer); null until it is seeded.
//   - arrangementGetterRef: live `() => api.toJSON()` reader the writer reads at flush time, so it
//     captures the current placement without re-rendering on every drag.
//   - requestSaveRef: the writer's save trigger, called on a Dockview arrangement change — which
//     doesn't dispatch to Redux, so the writer's store subscription would otherwise miss it.
export interface InstanceWorkspaceContextValue {
  currentSeed: InstanceWorkspaceSeed | null
  arrangementGetterRef: MutableRefObject<(() => Record<string, unknown> | null) | null>
  requestSaveRef: MutableRefObject<(() => void) | null>
}

const InstanceWorkspaceContext = createContext<InstanceWorkspaceContextValue | null>(null)

export function InstanceWorkspaceProvider({
  enabled,
  currentSeed,
  children,
}: {
  enabled: boolean
  currentSeed: InstanceWorkspaceSeed | null
  children: ReactNode
}) {
  const arrangementGetterRef = useRef<(() => Record<string, unknown> | null) | null>(null)
  const requestSaveRef = useRef<(() => void) | null>(null)
  const value = useMemo(
    () => (enabled ? { currentSeed, arrangementGetterRef, requestSaveRef } : null),
    [enabled, currentSeed],
  )
  return <InstanceWorkspaceContext.Provider value={value}>{children}</InstanceWorkspaceContext.Provider>
}

// Returns null outside the provider or when it is disabled (flag-off path).
export function useInstanceWorkspaceContext(): InstanceWorkspaceContextValue | null {
  return useContext(InstanceWorkspaceContext)
}
